import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { Category, Item } from '../../lib/supabase'
import { normalizeName } from '../../lib/items'
import { SheetPanel } from '../SheetPanel'
import { CategoryPills } from '../CategoryPills'
import { useLatest } from '../../hooks/useLatest'
import { BasketIcon, TrashIcon } from '../icons'
import { useAutoFocus } from '../../hooks/useAutoFocus'

// The item editor (ux/03 ItemEditor): a bottom sheet on the phone, a centred
// dialog when planning. Every edit applies live — there is no Save; Done (or
// Enter in any field) just closes. Delete runs the same delete + Undo as the
// swipe.
//
// 'add' is the flow where a basket item was picked from quick add to go back
// on the list with changes: nothing is written until "Add to list".

const WRITE_DELAY_MS = 250

interface Props {
  item: Item | null
  categories: Category[]
  mode: 'shop' | 'plan'
  intent?: 'add' | 'edit'
  tintFor: (categoryId: string | null) => string
  onUpdate: (id: string, name: string, quantity: string | null, category_id: string | null, restore: boolean, note?: string | null) => void
  /** Shopping mode only: the editor's Put in basket. */
  onPutInBasket?: (id: string) => void
  /** Raised over shopping mode. */
  zIndex?: number
  onDelete: (id: string, viaKeyboard: boolean) => void
  onClose: () => void
}

export function ItemEditor(props: Props) {
  return (
    <AnimatePresence>
      {props.item && <EditorBody key={props.item.id} {...props} item={props.item} />}
    </AnimatePresence>
  )
}

function EditorBody({
  item, categories, mode, intent = 'edit', tintFor, onUpdate, onPutInBasket, zIndex, onDelete, onClose,
}: Props & { item: Item }) {
  const adding = intent === 'add'
  const focusName = useAutoFocus<HTMLInputElement>(!adding && mode === 'plan')
  const focusQuantity = useAutoFocus<HTMLInputElement>(adding)
  // Seeded once per open: a realtime update from another member must not
  // overwrite what is being typed here.
  const [name, setName] = useState(item.name)
  const [quantity, setQuantity] = useState(item.quantity ?? '')
  const [note, setNote] = useState(item.note ?? '')
  const [categoryId, setCategoryId] = useState(item.category_id)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const latest = useLatest({ name, quantity, note, categoryId })
  const dirty = useRef(false)

  const write = () => {
    if (timer.current) clearTimeout(timer.current)
    if (!dirty.current || adding) return
    dirty.current = false
    const v = latest.current
    const clean = normalizeName(v.name)
    if (!clean) return // an emptied name keeps the old one
    onUpdate(item.id, clean, v.quantity.trim() || null, v.categoryId, false, v.note.trim() || null)
  }
  const schedule = (immediate = false) => {
    dirty.current = true
    if (timer.current) clearTimeout(timer.current)
    if (immediate) setTimeout(write, 0)
    else timer.current = setTimeout(write, WRITE_DELAY_MS)
  }
  // Flush whatever is pending when the editor goes away.
  const writeRef = useLatest(write)
  useEffect(() => () => writeRef.current(), [writeRef])

  function done() {
    if (adding) {
      const clean = normalizeName(name)
      if (clean) onUpdate(item.id, clean, quantity.trim() || null, categoryId, true, note.trim() || null)
    } else {
      write()
    }
    onClose()
  }

  const onEnter = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); done() } }

  return (
    <SheetPanel onClose={done} ariaLabel={adding ? 'Add to list' : 'Edit item'} layout="scroll" zIndex={zIndex}>
      <div className="ml-editor__head" style={{ '--ml-tint': tintFor(categoryId) } as CSSProperties}>
        <span className="ml-dot" aria-hidden="true" />
        <h2 className="ml-label ml-editor__title">{adding ? 'Add to list' : 'Edit item'}</h2>
        <button type="button" className="ml-btn ml-btn--primary ml-editor__done" onClick={done}>
          {adding ? 'Add' : 'Done'}
        </button>
      </div>

      <input
        className="ml-editor__name ml-display"
        aria-label="Name"
        value={name}
        ref={focusName}
        onChange={e => { setName(e.target.value); schedule() }}
        onKeyDown={onEnter}
      />

      <div className="ml-editor__fields">
        <label className="ml-boxfield">
          <span className="ml-label">Quantity</span>
          <input ref={focusQuantity} value={quantity} placeholder="e.g. 2, 500 g"
            onChange={e => { setQuantity(e.target.value); schedule() }} onKeyDown={onEnter} />
        </label>
        <label className="ml-boxfield">
          <span className="ml-label">Note</span>
          <input value={note} placeholder="brand, ripe…"
            onChange={e => { setNote(e.target.value); schedule() }} onKeyDown={onEnter} />
        </label>
      </div>

      <div className="ml-field">
        <span className="ml-label">Category</span>
        <CategoryPills categories={categories} value={categoryId} tintFor={tintFor}
          onPick={id => { setCategoryId(id); schedule(true) }} />
      </div>

      {!adding && (
        <div className="ml-editor__actions">
          {mode === 'shop' && onPutInBasket && !item.done && (
            <button type="button" className="ml-btn ml-btn--secondary ml-btn--row"
              onClick={() => { write(); onPutInBasket(item.id); onClose() }}>
              <BasketIcon size={18} /> Put in basket
            </button>
          )}
          <button type="button" className="ml-btn ml-btn--destructive ml-btn--row"
            onClick={e => { if (timer.current) clearTimeout(timer.current); dirty.current = false; onDelete(item.id, e.detail === 0); onClose() }}>
            <TrashIcon size={18} /> Delete
          </button>
        </div>
      )}
    </SheetPanel>
  )
}
